import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Loader from "components/DesignSystem/Loader/loader";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Info } from "components/icons/Info";
import { MagnifyingGlass } from "components/icons/MagnifyingGlass";
import { DISPLAY_DATE } from "constants/date";
import {
  CREDIT_NOTE_STATUS,
  CREDIT_NOTE_STATUS_TAG_TYPE,
  CREDIT_NOTE_STATUS_LABEL,
} from "constants/creditNotes";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import React, { useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import { useGetAllCreditNotesQuery, CreditNote } from "store/apis/creditNotes";
import { AddCreditNoteModal } from "components/AddCreditNoteModal/AddCreditNoteModal";

export const CreditNotesTabContent = ({
  customerId,
  selectedCreditNotes,
  setSelectedCreditNotes,
  pendingAmount,
}: {
  customerId: string;
  selectedCreditNotes: CreditNote[];
  setSelectedCreditNotes: React.Dispatch<React.SetStateAction<CreditNote[]>>;
  pendingAmount: number;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const addCreditNoteModal = useModal();

  const { data: creditNotesData, isLoading } = useGetAllCreditNotesQuery(
    {
      groupId,
      entityId,
      customerId,
      status: [
        CREDIT_NOTE_STATUS.AVAILABLE,
        CREDIT_NOTE_STATUS.PARTIALLY_APPLIED,
      ],
    },
    { skip: !customerId || !entityId || !groupId }
  );

  const creditNotes = creditNotesData?.customer_credit_debit_notes || [];

  const columnHelper = createColumnHelper<CreditNote>();

  const onRowClick = (row: Row<CreditNote>) => {
    if (!row.getCanSelect()) return;

    const isRowSelected = selectedCreditNotes.find(
      (cn) => cn.uuid === row.original.uuid
    );

    if (isRowSelected) {
      return setSelectedCreditNotes((prev) =>
        prev.filter((cn) => cn.uuid !== row.original.uuid)
      );
    }

    setSelectedCreditNotes((prev) => [...prev, row.original]);
  };

  const enableSelection = pendingAmount > 0;

  const selectedRows = selectedCreditNotes.reduce((acc, cn) => {
    acc[cn.uuid] = true;
    return acc;
  }, {} as Record<string, boolean>);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 5,
        enableSorting: false,
        header: () => <></>,
        cell: ({ row }) => {
          const isDisabled = !row.getCanSelect();

          return (
            <ConditionalToolTip
              condition={
                isDisabled &&
                "The total amount of selected items has already exceeded the invoice amount"
              }
            >
              <span>
                <Checkbox checked={row.getIsSelected()} disabled={isDisabled} />
              </span>
            </ConditionalToolTip>
          );
        },
      }),

      columnHelper.accessor("note_id", {
        id: "NOTE_ID",
        size: 10,
        header: "ID",
        enableSorting: false,
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("date", {
        id: "DATE",
        size: 15,
        header: "Creation Date",
        enableSorting: false,
        cell: (info) => (
          <div className="t-text-subtext t-text-text-30">
            {dayjs(info.getValue()).format(DISPLAY_DATE)}
          </div>
        ),
      }),

      columnHelper.accessor("description", {
        id: "DESCRIPTION",
        size: 25,
        header: "DESCRIPTION",
        enableSorting: false,
        cell: (info) => info.getValue() || "-",
      }),

      columnHelper.accessor("status", {
        id: "STATUS",
        size: 15,
        header: "Status",
        enableSorting: false,
        cell: (info) => (
          <Tag
            tagType={CREDIT_NOTE_STATUS_TAG_TYPE[info.getValue()]}
            size="small"
            rounded
            icon={false}
          >
            {CREDIT_NOTE_STATUS_LABEL[info.getValue()]}
          </Tag>
        ),
      }),

      columnHelper.accessor("amount", {
        id: "AMOUNT",
        size: 20,
        header: () => (
          <span className="t-flex t-justify-end t-ml-auto">Credit Amount</span>
        ),
        enableSorting: false,
        cell: (info) => {
          const amount = info.getValue();
          const remainingBalance = info.row.original.remaining_balance;
          const isPartiallyApplied =
            info.row.original.status === CREDIT_NOTE_STATUS.PARTIALLY_APPLIED;

          return (
            <div className="t-text-right">
              <AmountSuperScript amount={amount} />
              {isPartiallyApplied && remainingBalance !== amount && (
                <div className="t-text-text-30 t-text-body-sm t-flex t-gap-0.5 t-flex-wrap t-items-center t-justify-end">
                  <AmountSuperScript amount={remainingBalance} /> of{" "}
                  <AmountSuperScript amount={amount} /> yet to be linked
                  <ToolTip text="Remaining balance available to apply">
                    <span>
                      <Info size="14" />
                    </span>
                  </ToolTip>
                </div>
              )}
            </div>
          );
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    data: creditNotes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => {
      return row.getIsSelected() ? true : enableSelection;
    },
    state: {
      rowSelection: selectedRows,
    },
    getRowId: (row) => row.uuid,
    enableMultiRowSelection: true,
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  if (isLoading) {
    return (
      <div className="t-w-full t-h-full t-flex t-justify-center t-align-middle t-py-12">
        <Loader size="small" />
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-4 t-mt-4">
      <div className="t-flex t-justify-between t-items-center">
        <div className="t-flex t-items-center t-gap-0.5">
          <p className="t-text-subtext t-text-text-60 t-m-0">Credit notes</p>
          <ToolTip text="Issue a Credit Note to adjust advance payments and overpayments">
            <span className="t-text-text-30 t-flex t-items-center t-text-body-sm">
              <Info size="14" />
            </span>
          </ToolTip>
        </div>
        <Button
          onClick={addCreditNoteModal.open}
          size="small"
          className="t-mt-2"
        >
          + Add credit note
        </Button>
      </div>

      {creditNotes.length === 0 ? (
        <div className="t-w-full t-h-full t-flex t-flex-col t-justify-center t-items-center t-py-12 t-gap-2 t-text-i-neutral-10">
          <MagnifyingGlass size="149" />
          <div className="t-w-full t-h-full t-flex t-flex-col t-justify-center t-items-center t-gap-3">
            <div className="t-text-body-sm t-text-text-30">
              No credit notes found
            </div>
          </div>
        </div>
      ) : (
        <TableUI
          height="auto"
          table={table}
          size="regular"
          onRowClick={onRowClick}
        />
      )}
      <AddCreditNoteModal
        isOpen={addCreditNoteModal.isOpen}
        close={addCreditNoteModal.close}
        customerId={customerId}
        entityId={entityId!}
      />
    </div>
  );
};
